import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANG_META, SUPPORTED_LANGS, setLanguage } from "@/i18n";

export default function LanguageSwitcher({ compact = false }) {
    const { i18n, t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = i18n.language?.split("-")[0] || "en";
    const meta = LANG_META[current] || LANG_META.en;

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={t("language.switcher_aria")}
                data-testid="lang-switcher-button"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-2.5 py-1 text-[12px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
            >
                <Globe size={12} strokeWidth={2} className="text-[#737373]" />
                {compact ? current.toUpperCase() : meta.native}
            </button>
            {open && (
                <ul
                    role="listbox"
                    data-testid="lang-switcher-menu"
                    className="absolute right-0 z-30 mt-2 max-h-[360px] w-56 overflow-auto rounded-xl border border-[#E5E5E5] bg-white py-1 shadow-[0_8px_24px_rgba(10,10,10,0.06)]"
                >
                    {SUPPORTED_LANGS.map((lang) => {
                        const m = LANG_META[lang];
                        const active = lang === current;
                        return (
                            <li key={lang}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    onClick={async () => {
                                        await setLanguage(lang);
                                        setOpen(false);
                                    }}
                                    data-testid={`lang-switcher-pick-${lang}`}
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition ${
                                        active ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]"
                                    }`}
                                >
                                    <span>
                                        <span className="block text-[14px] font-medium text-[#0A0A0A]">
                                            {m.native}
                                        </span>
                                        <span className="block text-[10.5px] uppercase tracking-wider text-[#A3A3A3]">
                                            {m.english}
                                        </span>
                                    </span>
                                    {active && <Check size={13} strokeWidth={2.25} className="text-[#0A0A0A]" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
